/**
 * Warehouse — Interaktive Lager-Visualisierung mit Zonen, Toren und Picker-Tracking.
 *
 * Features:
 * - SVG-Lageplan mit Fachboden, Blocklager, Hochregal, Packbereich, Inbound
 * - Zonen-Auslastung (Kapazitaet/Belegung), Tor-Status
 * - Picker-Routen-Analyse mit SVG-Polylines, Animation, Metriken
 * - Tooltip mit Zone-Details, Klick fuer Detailansicht
 *
 * @page /warehouse
 * @roles admin, manager
 * @api /api/warehouse/:id/heatmap — Live-Heatmap-Daten
 * @api /api/warehouse/:id/routes — Picker-Routen + Metriken
 * @i18n warehouse
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Ruler, Package, BarChart3, Users, Zap, Clock, User, Settings, Play, Pause, SkipForward, Route, Lightbulb, Check, X, MapPin } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Badge } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API || '';
const HEATMAP_COLORS = [
 [51, 142, 220], // blue (cold)
 [34, 211, 238], // cyan
 [16, 185, 129], // green
 [245, 158, 11], // yellow/amber
 [239, 68, 68], // red (hot)
];

const ROUTE_COLORS = ['#338edc', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

// ─── Constants ───
const WH_W = 60; // meters width
const WH_H = 72; // meters depth
const SCALE = 12; // px per meter
const SVG_W = WH_W * SCALE;
const SVG_H = WH_H * SCALE;

const ZONES = [
 {
 id: 'fachboden', nameKey: 'warehouse.zone_fachboden', subKey: 'warehouse.zone_fachboden_sub',
 x: 3, y: 4, w: 15, h: 41, color: '#338edc', typeKey: 'warehouse.zone_type_fachboden',
 capacity: 1200, occupancy: 82, rackRows: 14, rackSpacing: 1.2,
 },
 {
 id: 'blocklager', nameKey: 'warehouse.zone_blocklager', subKey: 'warehouse.zone_blocklager_sub',
 x: 20, y: 4, w: 20, h: 26, color: '#6366F1', typeKey: 'warehouse.zone_type_boden',
 capacity: 480, occupancy: 71, blockRows: 5, blockCols: 8,
 },
 {
 id: 'hochregal', nameKey: 'warehouse.zone_hochregal', subKey: 'warehouse.zone_hochregal_sub',
 x: 42, y: 4, w: 15, h: 46, color: '#4D9CE0', typeKey: 'warehouse.zone_type_palette',
 capacity: 960, occupancy: 65, rackRows: 10, rackSpacing: 1.4,
 },
 {
 id: 'packbereich', nameKey: 'warehouse.zone_packbereich',
 x: 3, y: 50, w: 15, h: 15, color: '#22D3EE', typeKey: 'warehouse.zone_type_pack',
 capacity: 12, occupancy: 75, packTables: 6,
 },
 {
 id: 'inbound', nameKey: 'warehouse.zone_inbound',
 x: 20, y: 58, w: 20, h: 9, color: '#06b6d4', typeKey: 'warehouse.zone_type_transit',
 capacity: 40, occupancy: 45,
 },
 {
 id: 'ladestation', nameKey: 'warehouse.zone_ladestation',
 x: 41, y: 60, w: 5, h: 6, color: '#6b7280', typeKey: 'warehouse.zone_type_ladestation',
 capacity: 8, occupancy: 50,
 },
];

const GATES = Array.from({ length: 8 }, (_, i) => ({
 id: `tor-${i + 1}`, nameKey: 'warehouse.gate',
 x: 3 + i * 7, y: 68, w: 3, h: 3.5, color: '#94a3b8', gateNum: i + 1,
}));

const DEMO_PICKERS = [
 { id: 1, name: 'Marco M.', color: '#338edc', status: 'active', picksPerHour: 38, lastPick: '14:32' },
 { id: 2, name: 'Katharina M.', color: '#6366F1', status: 'active', picksPerHour: 42, lastPick: '14:31' },
 { id: 3, name: 'Oliver N.', color: '#22D3EE', status: 'pause', picksPerHour: 35, lastPick: '14:15' },
 { id: 4, name: 'Amil K.', color: '#4D9CE0', status: 'active', picksPerHour: 40, lastPick: '14:33' },
];

// Picker waypoints: fachboden → gang → packbereich → zurück
const PICKER_ROUTES = [
 [
 { x: 10, y: 15 }, { x: 10, y: 25 }, { x: 10, y: 35 }, { x: 10, y: 48 },
 { x: 10, y: 55 }, { x: 10, y: 48 }, { x: 10, y: 20 }, { x: 10, y: 10 },
 ],
 [
 { x: 8, y: 10 }, { x: 8, y: 20 }, { x: 8, y: 30 }, { x: 8, y: 48 },
 { x: 10, y: 57 }, { x: 8, y: 48 }, { x: 30, y: 15 }, { x: 8, y: 10 },
 ],
 [
 { x: 50, y: 10 }, { x: 50, y: 25 }, { x: 50, y: 40 }, { x: 30, y: 48 },
 { x: 12, y: 55 }, { x: 30, y: 48 }, { x: 50, y: 30 }, { x: 50, y: 10 },
 ],
 [
 { x: 30, y: 8 }, { x: 30, y: 18 }, { x: 15, y: 48 }, { x: 10, y: 58 },
 { x: 15, y: 48 }, { x: 48, y: 20 }, { x: 48, y: 35 }, { x: 30, y: 8 },
 ],
];

// ─── Demo Banner ───
function DemoBanner() {
 const { t } = useLanguage();
 const [dismissed, setDismissed] = useState(() => localStorage.getItem('wh_demo_banner') === '1');
 if (dismissed) return null;
 return (
 <div className="mx-6 mt-4 flex items-center gap-3 px-4 py-3 bg-sky-500/10 border border-sky-500/20 rounded-card text-sm text-sky-300">
 <span className="shrink-0">📡</span>
 <span className="flex-1">
 <strong>{t('warehouse.demo_banner_strong')}</strong> – {t('warehouse.demo_banner')}
 </span>
 <button
 onClick={() => { setDismissed(true); localStorage.setItem('wh_demo_banner', '1'); }}
 className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-sky-500/20 text-sky-400 transition-colors"
 >&times;</button>
 </div>
 );
}

// ─── Zone Tooltip ───
function ZoneTooltip({ zone, pos }) {
 const { t } = useLanguage();
 if (!zone) return null;
 const area = zone.w * zone.h;
 return (
 <div
 className="fixed z-50 pointer-events-none bg-surface-elevated border border-line rounded-card shadow-2xl p-3 min-w-[200px]"
 style={{ left: pos.x + 16, top: pos.y - 8 }}
 >
 <div className="flex items-center gap-2 mb-2">
 <div className="w-3 h-3 rounded" style={{ backgroundColor: zone.color }} />
 <span className="text-sm font-bold text-content">{t(zone.nameKey)}</span>
 </div>
 <div className="space-y-1 text-xs text-content-secondary">
 <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('warehouse.tooltip_type')}</span><span>{t(zone.typeKey || 'warehouse.zone_label')}</span></div>
 <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('warehouse.tooltip_area')}</span><span>{area} m²</span></div>
 {zone.capacity && <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('warehouse.tooltip_capacity')}</span><span>{t('warehouse.slots_label', { n: zone.capacity })}</span></div>}
 {zone.occupancy != null && (
 <div className="flex justify-between items-center">
 <span className="text-[var(--text-secondary)]">{t('warehouse.tooltip_occupancy')}</span>
 <div className="flex items-center gap-2">
 <div className="w-16 h-1.5 bg-surface-inset rounded-full overflow-hidden">
 <div className="h-full rounded-full" style={{
 width: `${zone.occupancy}%`,
 backgroundColor: '#338edc',
 }} />
 </div>
 <span className="font-mono">{zone.occupancy}%</span>
 </div>
 </div>
 )}
 </div>
 </div>
 );
}

// ─── SVG Rack Rows (for fachboden/hochregal) ───
function RackDetail({ zone }) {
 if (!zone.rackRows) return null;
 const rows = [];
 const spacing = zone.rackSpacing || 1.2;
 const rackW = zone.w - 2;
 for (let i = 0; i < zone.rackRows; i++) {
 const ry = zone.y + 1.5 + i * spacing * (zone.h - 3) / (zone.rackRows * spacing);
 rows.push(
 <rect key={i} x={(zone.x + 1) * SCALE} y={ry * SCALE} width={rackW * SCALE} height={3}
 fill={zone.color} opacity={0.35} rx={1} />
 );
 }
 return <>{rows}</>;
}

// ─── SVG Block Layout (for blocklager) ───
function BlockDetail({ zone }) {
 if (!zone.blockRows) return null;
 const blocks = [];
 const cols = zone.blockCols || 6;
 const rows = zone.blockRows || 4;
 const bw = (zone.w - 3) / cols;
 const bh = (zone.h - 3) / rows;
 for (let r = 0; r < rows; r++) {
 for (let c = 0; c < cols; c++) {
 blocks.push(
 <rect key={`${r}-${c}`}
 x={(zone.x + 1.5 + c * bw) * SCALE} y={(zone.y + 1.5 + r * bh) * SCALE}
 width={(bw - 0.5) * SCALE} height={(bh - 0.5) * SCALE}
 fill={zone.color} opacity={0.25} rx={2} />
 );
 }
 }
 return <>{blocks}</>;
}

// ─── SVG Pack Tables ───
function PackDetail({ zone }) {
 if (!zone.packTables) return null;
 const tables = [];
 const cols = 3;
 const rows = Math.ceil(zone.packTables / cols);
 const tw = (zone.w - 3) / cols;
 const th = (zone.h - 4) / rows;
 for (let i = 0; i < zone.packTables; i++) {
 const r = Math.floor(i / cols);
 const c = i % cols;
 tables.push(
 <rect key={i}
 x={(zone.x + 1.5 + c * tw) * SCALE} y={(zone.y + 2 + r * th) * SCALE}
 width={(tw - 1) * SCALE} height={(th - 1) * SCALE}
 fill={zone.color} opacity={0.4} rx={3} />
 );
 }
 // Conveyor belt line to right
 tables.push(
 <line key="conv"
 x1={(zone.x + zone.w) * SCALE} y1={(zone.y + zone.h / 2) * SCALE}
 x2={(zone.x + zone.w + 4) * SCALE} y2={(zone.y + zone.h / 2) * SCALE}
 stroke={zone.color} strokeWidth={3} strokeDasharray="8 4" opacity={0.6} />
 );
 return <>{tables}</>;
}

// ─── Grid Lines ───
function GridLines() {
 const lines = [];
 for (let x = 0; x <= WH_W; x += 5) {
 lines.push(
 <line key={`vg-${x}`} x1={x * SCALE} y1={0} x2={x * SCALE} y2={SVG_H}
 stroke="#334155" strokeWidth={x % 10 === 0 ? 0.8 : 0.3} opacity={x % 10 === 0 ? 0.4 : 0.2} />
 );
 }
 for (let y = 0; y <= WH_H; y += 5) {
 lines.push(
 <line key={`hg-${y}`} x1={0} y1={y * SCALE} x2={SVG_W} y2={y * SCALE}
 stroke="#334155" strokeWidth={y % 10 === 0 ? 0.8 : 0.3} opacity={y % 10 === 0 ? 0.4 : 0.2} />
 );
 }
 return <>{lines}</>;
}

// ─── Scale Markers ───
function ScaleMarkers() {
 const markers = [];
 for (let x = 0; x <= WH_W; x += 10) {
 markers.push(
 <text key={`xs-${x}`} x={x * SCALE} y={SVG_H + 14} fill="#64748b" fontSize={10} textAnchor="middle" fontFamily="inherit">
 {x}m
 </text>
 );
 }
 for (let y = 0; y <= WH_H; y += 10) {
 markers.push(
 <text key={`ys-${y}`} x={-8} y={y * SCALE + 4} fill="#64748b" fontSize={10} textAnchor="end" fontFamily="inherit">
 {y}m
 </text>
 );
 }
 return <>{markers}</>;
}

// ─── Route Layer (SVG polylines with arrows + pick points) ───
function RouteLayer({ routes, animStep, onHoverPoint, scale }) {
 // routes: [{ route: [...], color, pickerName }]
 // animStep: null = show all, number = show up to this step
 return (
 <>
 {/* Arrow marker defs */}
 <defs>
 {routes.map((r, ri) => (
 <marker key={ri} id={`arrow-${ri}`} markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
 <path d="M0,0 L6,2 L0,4" fill={r.color} opacity={0.8} />
 </marker>
 ))}
 </defs>
 {routes.map((r, ri) => {
 const pts = r.route.filter(p => p.x != null && p.y != null);
 const visible = animStep != null ? pts.slice(0, animStep + 1) : pts;
 if (visible.length < 1) return null;
 return (
 <g key={ri}>
 {/* Polyline path */}
 {visible.length >= 2 && (
 <polyline
 points={visible.map(p => `${p.x * scale},${p.y * scale}`).join(' ')}
 fill="none" stroke={r.color} strokeWidth={2.5}
 strokeLinejoin="round" strokeLinecap="round"
 opacity={0.7} markerMid={`url(#arrow-${ri})`}
 />
 )}
 {/* Pick points */}
 {visible.map((p, pi) => {
 const isStart = pi === 0;
 const isEnd = pi === visible.length - 1 && (animStep == null || animStep >= pts.length - 1);
 const isCurrent = animStep != null && pi === animStep;
 const fillColor = isStart ? '#10B981' : isEnd ? '#EF4444' : r.color;
 const radius = isStart || isEnd || isCurrent ? 6 : 3.5;
 return (
 <g key={pi}>
 <circle
 cx={p.x * scale} cy={p.y * scale} r={radius}
 fill={fillColor} stroke="#fff" strokeWidth={isStart || isEnd ? 2 : 1}
 opacity={0.9}
 onMouseEnter={(e) => onHoverPoint({ ...p, step: pi + 1, color: r.color, pickerName: r.pickerName }, e)}
 onMouseLeave={() => onHoverPoint(null)}
 className="cursor-pointer"
 />
 {/* Pulse on current animation point */}
 {isCurrent && (
 <circle cx={p.x * scale} cy={p.y * scale} r={12}
 fill="none" stroke={r.color} strokeWidth={1.5} opacity={0.5}>
 <animate attributeName="r" from="6" to="18" dur="1s" repeatCount="indefinite" />
 <animate attributeName="opacity" from="0.6" to="0" dur="1s" repeatCount="indefinite" />
 </circle>
 )}
 {/* Start/End labels */}
 {isStart && (
 <text x={p.x * scale + 10} y={p.y * scale + 4} fill="#10B981" fontSize={9} fontWeight={700} fontFamily="inherit">START</text>
 )}
 {isEnd && (
 <text x={p.x * scale + 10} y={p.y * scale + 4} fill="#EF4444" fontSize={9} fontWeight={700} fontFamily="inherit">END</text>
 )}
 </g>
 );
 })}
 {/* Picker name label near start */}
 {visible.length > 0 && routes.length > 1 && (
 <text x={visible[0].x * scale} y={visible[0].y * scale - 10}
 fill={r.color} fontSize={10} fontWeight={700} textAnchor="middle" fontFamily="inherit">
 {r.pickerName}
 </text>
 )}
 </g>
 );
 })}
 </>
 );
}

// ─── Route Point Tooltip ───
function RoutePointTooltip({ point, pos }) {
 if (!point) return null;
 const ts = point.timestamp ? new Date(point.timestamp) : null;
 return (
 <div
 className="fixed z-50 pointer-events-none bg-surface-elevated border border-line rounded-card shadow-2xl p-2.5 min-w-[160px]"
 style={{ left: pos.x + 16, top: pos.y - 8 }}
 >
 <div className="flex items-center gap-2 mb-1">
 <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: point.color }} />
 <span className="text-xs font-bold text-content font-mono">{point.location}</span>
 </div>
 <div className="space-y-0.5 text-[10px] text-content-secondary">
 <div>Step {point.step}</div>
 {ts && <div>{ts.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>}
 {point.pickerName && <div>{point.pickerName}</div>}
 </div>
 </div>
 );
}

// ─── Demo Picker Layer (fallback when no real data) ───
function DemoPickerLayer({ pickers, selected, onSelect, scale }) {
 return (
 <>
 {pickers.map(p => {
 const isSel = selected === p.id;
 const trail = p.trail || [];
 return (
 <g key={p.id} className="cursor-pointer" onClick={() => onSelect(isSel ? null : p.id)}>
 {trail.length >= 2 && (
 <polyline
 points={trail.map(t => `${t.x * scale},${t.y * scale}`).join(' ')}
 fill="none" stroke={p.color} strokeWidth={isSel ? 2 : 1}
 strokeDasharray={isSel ? 'none' : '4 3'}
 opacity={isSel ? 0.7 : 0.3}
 />
 )}
 <circle cx={p.pos.x * scale} cy={p.pos.y * scale} r={isSel ? 10 : 8}
 fill={p.color} stroke="#fff" strokeWidth={isSel ? 2.5 : 1.5}
 className="transition-all duration-[3000ms] ease-in-out" />
 {isSel && (
 <circle cx={p.pos.x * scale} cy={p.pos.y * scale} r={16}
 fill="none" stroke={p.color} strokeWidth={1.5} opacity={0.5}>
 <animate attributeName="r" from="10" to="22" dur="1.5s" repeatCount="indefinite" />
 <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite" />
 </circle>
 )}
 <text x={p.pos.x * scale + 14} y={p.pos.y * scale + 4}
 fill="#e2e8f0" fontSize={11} fontWeight={600} fontFamily="inherit"
 className="transition-all duration-[3000ms] ease-in-out"
 >{p.name}</text>
 </g>
 );
 })}
 </>
 );
}

// ─── Heatmap Canvas Overlay ───
function interpolateColor(t) {
 // t: 0..1 → blend through HEATMAP_COLORS
 const n = HEATMAP_COLORS.length - 1;
 const i = Math.min(Math.floor(t * n), n - 1);
 const f = t * n - i;
 const a = HEATMAP_COLORS[i], b = HEATMAP_COLORS[i + 1];
 return [
 Math.round(a[0] + (b[0] - a[0]) * f),
 Math.round(a[1] + (b[1] - a[1]) * f),
 Math.round(a[2] + (b[2] - a[2]) * f),
 ];
}

function HeatmapCanvas({ points, whW, whH, scale, opacity }) {
 const canvasRef = useRef(null);
 const cW = whW * scale;
 const cH = whH * scale;
 const radius = 4 * scale; // 4m radius

 useEffect(() => {
 const canvas = canvasRef.current;
 if (!canvas) return;
 const ctx = canvas.getContext('2d');
 canvas.width = cW;
 canvas.height = cH;
 ctx.clearRect(0, 0, cW, cH);

 if (!points || points.length === 0) return;

 // Pass 1: Draw intensity as grayscale radial gradients
 for (const p of points) {
 if (p.x == null || p.y == null) continue;
 const px = p.x * scale;
 const py = p.y * scale;
 const grad = ctx.createRadialGradient(px, py, 0, px, py, radius);
 grad.addColorStop(0, `rgba(0,0,0,${p.intensity})`);
 grad.addColorStop(1, 'rgba(0,0,0,0)');
 ctx.fillStyle = grad;
 ctx.fillRect(px - radius, py - radius, radius * 2, radius * 2);
 }

 // Pass 2: Colorize — remap gray → color gradient
 const imageData = ctx.getImageData(0, 0, cW, cH);
 const data = imageData.data;
 for (let i = 0; i < data.length; i += 4) {
 const alpha = data[i + 3]; // intensity stored in alpha from grayscale
 if (alpha === 0) continue;
 const t = data[i + 3] / 255; // normalize
 const [r, g, b] = interpolateColor(t);
 data[i] = r;
 data[i + 1] = g;
 data[i + 2] = b;
 data[i + 3] = Math.round(alpha * 0.75); // slightly transparent
 }
 ctx.putImageData(imageData, 0, 0);
 }, [points, cW, cH, scale, radius]);

 return (
 <foreignObject x={0} y={0} width={cW} height={cH}>
 <canvas
 ref={canvasRef}
 width={cW} height={cH}
 style={{ width: cW, height: cH, opacity, pointerEvents: 'none' }}
 />
 </foreignObject>
 );
}

// ─── Heatmap Legend ───
function HeatmapLegend({ t }) {
 return (
 <div className="flex items-center gap-2 text-[10px] text-content-secondary">
 <span>{t('warehouse.heatmap.low')}</span>
 <div className="flex h-3 rounded overflow-hidden">
 {[0, 0.25, 0.5, 0.75, 1].map((v, i) => {
 const [r, g, b] = interpolateColor(v);
 return <div key={i} className="w-6 h-full" style={{ backgroundColor: `rgb(${r},${g},${b})` }} />;
 })}
 </div>
 <span>{t('warehouse.heatmap.high')}</span>
 </div>
 );
}

// ─── Heatmap Panel (right sidebar) ───
function HeatmapPanel({ data, period, setPeriod, pickerId, setPickerId, heatOpacity, setHeatOpacity, t, locale }) {
 const ZONE_COLORS = ['#338edc', '#6366F1', '#22D3EE', '#06b6d4', '#8b5cf6', '#94a3b8'];
 const topPoints = (data?.points || []).filter(p => p.x != null).slice(0, 10);
 const deadZones = data?.dead_zones || [];
 const zoneSummary = data?.zone_summary || [];
 const pickers = data?.pickers || [];

 return (
 <div className="w-[260px] shrink-0 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
 {/* Period selector */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.heatmap.period')}</div>
 <div className="flex flex-wrap gap-1">
 {[
 { key: 'today', label: t('warehouse.heatmap.today') },
 { key: 'week', label: t('warehouse.heatmap.week') },
 { key: 'month', label: t('warehouse.heatmap.month') },
 ].map(p => (
 <button key={p.key} onClick={() => setPeriod(p.key)}
 className={`px-2.5 py-1 text-[10px] font-medium rounded-lg transition-colors ${
 period === p.key ? 'bg-surface-active text-content shadow-sm' : 'text-content-secondary hover:text-content bg-surface-inset'
 }`}
 >{p.label}</button>
 ))}
 </div>
 </div>

 {/* Picker filter */}
 {pickers.length > 0 && (
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.heatmap.picker')}</div>
 <select value={pickerId || ''} onChange={e => setPickerId(e.target.value || null)}
 className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1.5 text-xs text-content focus:outline-none">
 <option value="">{t('warehouse.heatmap.allPickers')}</option>
 {pickers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
 </select>
 </div>
 )}

 {/* Opacity slider */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="flex items-center justify-between mb-2">
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.heatmap.opacity')}</span>
 <span className="text-[10px] font-mono text-content-tertiary">{Math.round(heatOpacity * 100)}%</span>
 </div>
 <input type="range" min={0.1} max={1} step={0.05} value={heatOpacity}
 onChange={e => setHeatOpacity(parseFloat(e.target.value))}
 className="w-full h-1.5 rounded-full appearance-none bg-surface-inset cursor-pointer accent-[#338edc]"
 />
 </div>

 {/* Summary stats */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="flex items-center gap-2 mb-1">
 <Zap className="w-4 h-4 text-[#F59E0B]" />
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.heatmap.picks')}</span>
 </div>
 <div className="text-lg font-mono font-bold text-content">{(data?.total_picks || 0).toLocaleString(locale)}</div>
 <div className="text-[10px] text-content-tertiary">{(data?.points || []).length} locations</div>
 </div>

 {/* Zone distribution donut */}
 {zoneSummary.length > 0 && (
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.heatmap.zoneDistribution')}</div>
 <div className="h-[120px]">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie data={zoneSummary} dataKey="picks" nameKey="zone" cx="50%" cy="50%"
 innerRadius={28} outerRadius={48} paddingAngle={2}>
 {zoneSummary.map((z, i) => (
 <Cell key={z.zone} fill={ZONE_COLORS[i % ZONE_COLORS.length]} />
 ))}
 </Pie>
 <ReTooltip
 contentStyle={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 8, fontSize: 11 }}
 formatter={(v, name) => [`${v} picks`, name]}
 />
 </PieChart>
 </ResponsiveContainer>
 </div>
 <div className="space-y-1 mt-1">
 {zoneSummary.map((z, i) => (
 <div key={z.zone} className="flex items-center gap-2 text-[10px]">
 <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: ZONE_COLORS[i % ZONE_COLORS.length] }} />
 <span className="text-content-secondary flex-1">{z.zone}</span>
 <span className="font-mono text-content-tertiary">{z.percentage}%</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Top 10 locations */}
 {topPoints.length > 0 && (
 <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
 <div className="px-3 py-2.5 border-b border-line">
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.heatmap.topLocations')}</span>
 </div>
 <div className="divide-y divide-line">
 {topPoints.map((p, i) => (
 <div key={p.location} className="flex items-center gap-2 px-3 py-1.5">
 <span className="text-[10px] font-mono text-content-tertiary w-4">{i + 1}.</span>
 <span className="text-[11px] font-mono text-content flex-1 truncate">{p.location}</span>
 <span className="text-[10px] font-mono text-content-secondary">{p.count}</span>
 <div className="w-12 h-1 bg-surface-inset rounded-full overflow-hidden">
 <div className="h-full rounded-full" style={{
 width: `${p.intensity * 100}%`,
 backgroundColor: `rgb(${interpolateColor(p.intensity).join(',')})`,
 }} />
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Dead zones */}
 {deadZones.length > 0 && (
 <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
 <div className="px-3 py-2.5 border-b border-line">
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.heatmap.deadZones')}</span>
 </div>
 <div className="divide-y divide-line">
 {deadZones.slice(0, 5).map(d => (
 <div key={d.location} className="flex items-center gap-2 px-3 py-1.5">
 <span className="text-[11px] font-mono text-content-tertiary flex-1 truncate">{d.location}</span>
 <span className="text-[10px] text-content-tertiary">
 {d.last_pick ? new Date(d.last_pick).toLocaleDateString(locale) : '—'}
 </span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Legend */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.heatmap.legend')}</div>
 <HeatmapLegend t={t} />
 {data?.total_picks === 0 && (
 <p className="text-[10px] text-content-tertiary mt-2">Noch keine Location-Daten vorhanden. Daten werden beim nächsten Wave-Sync erfasst.</p>
 )}
 </div>
 </div>
 );
}

// ─── Info Panel ───
function InfoPanel({ zones }) {
 const { t, locale } = useLanguage();
 const [expanded, setExpanded] = useState(null);
 const totalArea = WH_W * WH_H;
 const totalCapacity = zones.reduce((s, z) => s + (z.capacity || 0), 0);
 const avgOccupancy = Math.round(zones.reduce((s, z) => s + (z.occupancy || 0), 0) / zones.length);

 const stats = [
 { Icon: Ruler, label: t('warehouse.stat_area'), value: `${totalArea.toLocaleString(locale)} m²`, sub: `${WH_W}×${WH_H}m`, color: 'text-[#338edc]' },
 { Icon: Package, label: t('warehouse.stat_storage'), value: `~${totalCapacity.toLocaleString(locale)}`, color: 'text-[#6366F1]' },
 { Icon: BarChart3, label: t('warehouse.stat_occupancy'), value: `${avgOccupancy}%`, bar: avgOccupancy, color: 'text-[#22D3EE]' },
 { Icon: Users, label: t('warehouse.stat_active_pickers'), value: '4', color: 'text-[#10B981]' },
 { Icon: Zap, label: t('warehouse.stat_picks_today'), value: '342', color: 'text-[#F59E0B]' },
 { Icon: Clock, label: t('warehouse.stat_avg_pick_time'), value: '45s', color: 'text-[#338edc]' },
 ];

 return (
 <div className="w-[260px] shrink-0 space-y-3">
 {/* Stats cards */}
 {stats.map(s => (
 <div key={s.label} className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="flex items-center gap-2 mb-1">
 <s.Icon className={`w-4 h-4 ${s.color}`} />
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{s.label}</span>
 </div>
 <div className="text-lg font-mono font-bold text-content">{s.value}</div>
 {s.sub && <div className="text-[10px] text-content-tertiary">{s.sub}</div>}
 {s.bar != null && (
 <div className="mt-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
 <div className="h-full rounded-full transition-all" style={{
 width: `${s.bar}%`,
 backgroundColor: '#338edc',
 }} />
 </div>
 )}
 </div>
 ))}

 {/* Zone breakdown */}
 <div className="bg-surface-card border border-line rounded-card shadow-card overflow-hidden">
 <div className="px-3 py-2.5 border-b border-line">
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.zone_breakdown')}</span>
 </div>
 <div className="divide-y divide-line">
 {zones.map(z => {
 const area = z.w * z.h;
 const isOpen = expanded === z.id;
 return (
 <div key={z.id}>
 <button
 onClick={() => setExpanded(isOpen ? null : z.id)}
 className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover transition-colors"
 >
 <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: z.color }} />
 <span className="text-xs font-medium text-content flex-1 truncate">{t(z.nameKey)}</span>
 <svg className={`w-3 h-3 text-[var(--text-secondary)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
 fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
 <path d="M19 9l-7 7-7-7" />
 </svg>
 </button>
 {isOpen && (
 <div className="px-3 pb-2 space-y-1 text-[11px]">
 <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('warehouse.zone_area')}</span><span className="text-content-secondary">{area} m²</span></div>
 {z.capacity && <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('warehouse.zone_slots')}</span><span className="text-content-secondary">~{z.capacity}</span></div>}
 {z.occupancy != null && (
 <div className="flex justify-between items-center">
 <span className="text-[var(--text-secondary)]">{t('warehouse.zone_occupancy')}</span>
 <div className="flex items-center gap-1.5">
 <div className="w-12 h-1 bg-surface-inset rounded-full overflow-hidden">
 <div className="h-full rounded-full" style={{
 width: `${z.occupancy}%`,
 backgroundColor: z.color,
 }} />
 </div>
 <span className="text-content-secondary font-mono">{z.occupancy}%</span>
 </div>
 </div>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 </div>
 );
}

// ─── Route Panel (right side in picker/route view) ───
function RoutePanel({ routeData, compareData, availablePickers, pickerId, setPickerId,
 comparePickerId, setComparePickerId, routeDate, setRouteDate,
 isPlaying, setIsPlaying, playSpeed, setPlaySpeed, animStep, setAnimStep, t, locale, isDemo }) {

 const metrics = routeData?.metrics;
 const compareMetrics = compareData?.metrics;

 function effBadge(val) {
 if (val >= 85) return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
 if (val >= 70) return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
 return 'bg-red-500/15 text-red-400 border-red-500/30';
 }

 function MetricRow({ label, value, compareVal, unit, badge }) {
 return (
 <div className="flex items-center justify-between py-1">
 <span className="text-[10px] text-[var(--text-secondary)]">{label}</span>
 <div className="flex items-center gap-2">
 {badge ? (
 <span className={`px-1.5 py-0.5 text-[10px] font-mono font-bold rounded border ${effBadge(value)}`}>
 {value}{unit}
 </span>
 ) : (
 <span className="text-xs font-mono font-bold text-content">{value}{unit}</span>
 )}
 {compareVal != null && (
 <span className="text-[10px] font-mono text-content-tertiary">vs {compareVal}{unit}</span>
 )}
 </div>
 </div>
 );
 }

 const totalSteps = routeData?.route?.filter(p => p.x != null).length || 0;

 return (
 <div className="w-[260px] shrink-0 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
 {/* Date picker */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.routes.date')}</div>
 <input type="date" value={routeDate} onChange={e => setRouteDate(e.target.value)}
 className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1.5 text-xs text-content focus:outline-none" />
 </div>

 {/* Picker selector */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.routes.picker')}</div>
 <select value={pickerId || ''} onChange={e => setPickerId(e.target.value || null)}
 className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1.5 text-xs text-content focus:outline-none">
 <option value="">{t('warehouse.routes.allPickers')}</option>
 {availablePickers.map(p => (
 <option key={p.id} value={p.id}>{p.name} ({p.picks} picks)</option>
 ))}
 </select>
 </div>

 {/* Animation controls */}
 {metrics && totalSteps > 1 && (
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.routes.animation')}</div>
 <div className="flex items-center gap-2 mb-2">
 <button onClick={() => setIsPlaying(!isPlaying)}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset text-content-secondary hover:text-content transition-colors">
 {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
 </button>
 <button onClick={() => setAnimStep(s => s != null ? Math.min(totalSteps - 1, s + 1) : 0)}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset text-content-secondary hover:text-content transition-colors">
 <SkipForward className="w-3.5 h-3.5" />
 </button>
 <div className="flex bg-surface-inset rounded-lg p-0.5 gap-0.5">
 {[1, 2, 5, 10].map(s => (
 <button key={s} onClick={() => setPlaySpeed(s)}
 className={`px-1.5 py-0.5 text-[9px] font-mono rounded transition-colors ${
 playSpeed === s ? 'bg-surface-active text-content shadow-sm' : 'text-content-tertiary'
 }`}>{s}x</button>
 ))}
 </div>
 </div>
 {/* Timeline slider */}
 <div className="flex items-center gap-2">
 <input type="range" min={0} max={totalSteps - 1} value={animStep ?? totalSteps - 1}
 onChange={e => { setIsPlaying(false); setAnimStep(parseInt(e.target.value)); }}
 className="flex-1 h-1.5 rounded-full appearance-none bg-surface-inset cursor-pointer accent-[#338edc]"
 />
 <span className="text-[9px] font-mono text-content-tertiary w-10 text-right">
 {(animStep ?? totalSteps)}/{totalSteps}
 </span>
 </div>
 </div>
 )}

 {/* Metrics */}
 {metrics && (
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="flex items-center gap-2 mb-2">
 <Route className="w-4 h-4 text-[#6366F1]" />
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.routes.title')}</span>
 </div>
 <div className="text-sm font-medium text-content mb-2">
 {routeData.picker?.name}
 </div>
 <div className="space-y-0.5 divide-y divide-line">
 <MetricRow label={t('warehouse.routes.totalPicks')} value={metrics.total_picks} compareVal={compareMetrics?.total_picks} unit="" />
 <MetricRow label={t('warehouse.routes.totalDistance')} value={`${metrics.total_distance_m.toLocaleString(locale)}`} compareVal={compareMetrics ? `${compareMetrics.total_distance_m.toLocaleString(locale)}` : null} unit="m" />
 <MetricRow label={t('warehouse.routes.optimalDistance')} value={`${metrics.optimal_distance_m.toLocaleString(locale)}`} unit="m" />
 <MetricRow label={t('warehouse.routes.efficiency')} value={metrics.efficiency_ratio} compareVal={compareMetrics?.efficiency_ratio} unit="%" badge />
 <MetricRow label={t('warehouse.routes.zoneChanges')} value={metrics.zone_changes} compareVal={compareMetrics?.zone_changes} unit="" />
 <MetricRow label={t('warehouse.routes.zigzagScore')} value={Math.round(metrics.zigzag_score * 100)} compareVal={compareMetrics ? Math.round(compareMetrics.zigzag_score * 100) : null} unit="%" />
 <MetricRow label={t('warehouse.routes.duration')}
 value={metrics.duration_minutes >= 60 ? `${Math.floor(metrics.duration_minutes / 60)}h ${Math.round(metrics.duration_minutes % 60)}min` : `${metrics.duration_minutes}min`}
 unit="" />
 <MetricRow label={t('warehouse.routes.avgTimeBetweenPicks')} value={`${metrics.avg_time_between_picks_sec}`} unit="s" />
 </div>
 </div>
 )}

 {/* Compare picker */}
 {pickerId && availablePickers.length > 1 && (
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-2">{t('warehouse.routes.compareWith')}</div>
 <select value={comparePickerId || ''} onChange={e => setComparePickerId(e.target.value || null)}
 className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-1.5 text-xs text-content focus:outline-none">
 <option value="">—</option>
 {availablePickers.filter(p => String(p.id) !== String(pickerId)).map(p => (
 <option key={p.id} value={p.id}>{p.name}</option>
 ))}
 </select>
 {compareData?.picker && (
 <div className="mt-2 text-[10px] text-content-secondary flex items-center gap-1.5">
 <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ROUTE_COLORS[1] }} />
 {compareData.picker.name}
 </div>
 )}
 </div>
 )}

 {/* Demo mode hint */}
 {isDemo && (
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <p className="text-[10px] text-content-tertiary">
 {t('warehouse.demo_banner_strong')}: {t('warehouse.routes.noData')}
 </p>
 </div>
 )}
 </div>
 );
}

// ─── Optimization Layer (problem lines on floorplan) ───
function OptimizationLayer({ problemLines, highlightPair, scale }) {
 if (!problemLines || problemLines.length === 0) return null;
 const maxScore = Math.max(...problemLines.map(p => parseFloat(p.problem_score) || 1));
 return (
 <>
 {problemLines.map((p, i) => {
 if (!p.from_x || !p.from_y || !p.to_x || !p.to_y) return null;
 const intensity = (parseFloat(p.problem_score) || 0) / maxScore;
 const isHighlighted = highlightPair &&
 ((p.location_a === highlightPair.from && p.location_b === highlightPair.to) ||
 (p.location_a === highlightPair.to && p.location_b === highlightPair.from));
 const color = intensity > 0.7 ? '#EF4444' : intensity > 0.4 ? '#F59E0B' : '#338edc';
 return (
 <g key={i}>
 <line
 x1={parseFloat(p.from_x) * scale} y1={parseFloat(p.from_y) * scale}
 x2={parseFloat(p.to_x) * scale} y2={parseFloat(p.to_y) * scale}
 stroke={color} strokeWidth={isHighlighted ? 3 : 1.5}
 strokeDasharray={isHighlighted ? 'none' : '6 3'}
 opacity={isHighlighted ? 0.9 : 0.4 + intensity * 0.3}
 />
 {/* Problem score label at midpoint */}
 {isHighlighted && (
 <text
 x={(parseFloat(p.from_x) + parseFloat(p.to_x)) / 2 * scale}
 y={(parseFloat(p.from_y) + parseFloat(p.to_y)) / 2 * scale - 6}
 fill={color} fontSize={9} fontWeight={700} textAnchor="middle" fontFamily="inherit"
 >
 {Math.round(parseFloat(p.problem_score))}
 </text>
 )}
 {/* Location dots */}
 <circle cx={parseFloat(p.from_x) * scale} cy={parseFloat(p.from_y) * scale} r={isHighlighted ? 5 : 3}
 fill={color} opacity={isHighlighted ? 0.9 : 0.5} />
 <circle cx={parseFloat(p.to_x) * scale} cy={parseFloat(p.to_y) * scale} r={isHighlighted ? 5 : 3}
 fill={color} opacity={isHighlighted ? 0.9 : 0.5} />
 {isHighlighted && (
 <>
 <text x={parseFloat(p.from_x) * scale} y={parseFloat(p.from_y) * scale - 8}
 fill="#fff" fontSize={8} fontWeight={600} textAnchor="middle" fontFamily="inherit">{p.location_a}</text>
 <text x={parseFloat(p.to_x) * scale} y={parseFloat(p.to_y) * scale - 8}
 fill="#fff" fontSize={8} fontWeight={600} textAnchor="middle" fontFamily="inherit">{p.location_b}</text>
 </>
 )}
 </g>
 );
 })}
 </>
 );
}

// ─── Optimization Panel (right sidebar) ───
function OptimizationPanel({ suggestions, problemLines, generating, onGenerate, onUpdateStatus, highlightPair, setHighlightPair, t, locale }) {
 const totalProblems = (problemLines || []).length;
 const pendingCount = suggestions.filter(s => s.status === 'pending').length;
 const acceptedCount = suggestions.filter(s => s.status === 'accepted').length;

 function priorityBadge(p) {
 if (p === 'high') return 'bg-red-500/15 text-red-400 border-red-500/30';
 if (p === 'medium') return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
 return 'bg-sky-500/15 text-sky-400 border-sky-500/30';
 }

 return (
 <div className="w-[280px] shrink-0 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
 {/* Summary stats */}
 <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
 <div className="flex items-center gap-2 mb-2">
 <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
 <span className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{t('warehouse.optimization.title')}</span>
 </div>
 <div className="grid grid-cols-3 gap-2 text-center">
 <div>
 <div className="text-lg font-mono font-bold text-content">{totalProblems}</div>
 <div className="text-[9px] text-content-tertiary">{t('warehouse.optimization.problems')}</div>
 </div>
 <div>
 <div className="text-lg font-mono font-bold text-content">{pendingCount}</div>
 <div className="text-[9px] text-content-tertiary">{t('warehouse.optimization.pending')}</div>
 </div>
 <div>
 <div className="text-lg font-mono font-bold text-content">{acceptedCount}</div>
 <div className="text-[9px] text-content-tertiary">{t('warehouse.optimization.accepted')}</div>
 </div>
 </div>
 </div>

 {/* Generate button */}
 <button
 onClick={onGenerate}
 disabled={generating}
 className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#338edc] hover:bg-[#2a7bc5] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {generating ? (
 <>
 <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
 {t('warehouse.optimization.generating')}
 </>
 ) : (
 <>
 <Lightbulb className="w-4 h-4" />
 {t('warehouse.optimization.generate')}
 </>
 )}
 </button>

 {/* Suggestion cards */}
 {suggestions.length === 0 && !generating && (
 <div className="bg-surface-card border border-line rounded-card p-4 shadow-card text-center">
 <Lightbulb className="w-8 h-8 mx-auto text-content-tertiary mb-2 opacity-40" />
 <p className="text-xs text-content-tertiary">{t('warehouse.optimization.noSuggestions')}</p>
 </div>
 )}

 {suggestions.map(s => {
 const isActive = highlightPair?.from === s.from_location && highlightPair?.to === s.to_location;
 return (
 <div key={s.id}
 className={`bg-surface-card border rounded-card p-3 shadow-card transition-colors ${
 isActive ? 'border-[#338edc]' : 'border-line'
 } ${s.status === 'accepted' ? 'opacity-60' : s.status === 'rejected' ? 'opacity-40' : ''}`}
 >
 {/* Priority + Status */}
 <div className="flex items-center gap-2 mb-2">
 <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border ${priorityBadge(s.priority)}`}>
 {s.priority?.toUpperCase()}
 </span>
 {s.status !== 'pending' && (
 <span className={`px-1.5 py-0.5 text-[9px] font-mono rounded ${
 s.status === 'accepted' ? 'bg-emerald-500/15 text-emerald-400' :
 s.status === 'implemented' ? 'bg-blue-500/15 text-blue-400' :
 'bg-[rgba(107,114,128,0.15)] text-[var(--text-tertiary,#6B7280)]'
 }`}>{s.status}</span>
 )}
 </div>

 {/* From → To */}
 <div className="flex items-center gap-2 mb-1.5">
 <span className="text-[11px] font-mono font-bold text-content">{s.from_location}</span>
 <span className="text-content-tertiary text-[10px]">→</span>
 <span className="text-[11px] font-mono font-bold text-content">{s.to_location || '?'}</span>
 </div>

 {/* Reason */}
 <p className="text-[10px] text-content-secondary leading-relaxed mb-2">{s.reason}</p>

 {/* Metrics */}
 <div className="flex items-center gap-3 text-[9px] text-content-tertiary mb-2">
 {s.expected_saving_meters && <span>~{s.expected_saving_meters}m {t('warehouse.optimization.saved')}</span>}
 {s.affected_picks_month && <span>{s.affected_picks_month} picks/mo</span>}
 </div>

 {/* Actions */}
 {s.status === 'pending' && (
 <div className="flex items-center gap-1.5">
 <button onClick={() => onUpdateStatus(s.id, 'accepted')}
 className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors">
 <Check className="w-3 h-3" /> {t('warehouse.optimization.accept')}
 </button>
 <button onClick={() => onUpdateStatus(s.id, 'rejected')}
 className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors">
 <X className="w-3 h-3" /> {t('warehouse.optimization.reject')}
 </button>
 <button onClick={() => setHighlightPair(isActive ? null : { from: s.from_location, to: s.to_location })}
 className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg transition-colors ${
 isActive ? 'bg-[#338edc]/25 text-[#338edc]' : 'bg-surface-inset text-content-secondary hover:text-content'
 }`}>
 <MapPin className="w-3 h-3" /> {t('warehouse.optimization.showOnMap')}
 </button>
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
}

// ═════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════
export default function Warehouse() {
 const { t, locale } = useLanguage();
 const navigate = useNavigate();
 const [view, setView] = useState('layout');
 const [warehouse, setWarehouse] = useState('AT');
 const [hoveredZone, setHoveredZone] = useState(null);
 const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
 const [selectedPicker, setSelectedPicker] = useState(null);

 // Heatmap state
 const [heatmapData, setHeatmapData] = useState(null);
 const [heatPeriod, setHeatPeriod] = useState('today');
 const [heatPickerId, setHeatPickerId] = useState(null);
 const [heatOpacity, setHeatOpacity] = useState(0.7);

 // Route state
 const [routeDate, setRouteDate] = useState(new Date().toISOString().slice(0, 10));
 const [routePickerId, setRoutePickerId] = useState(null);
 const [comparePickerId, setComparePickerId] = useState(null);
 const [routeData, setRouteData] = useState(null);
 const [compareData, setCompareData] = useState(null);
 const [availablePickers, setAvailablePickers] = useState([]);
 const [animStep, setAnimStep] = useState(null);
 const [isPlaying, setIsPlaying] = useState(false);
 const [playSpeed, setPlaySpeed] = useState(1);
 const [routeTooltipPoint, setRouteTooltipPoint] = useState(null);
 const [routeTooltipPos, setRouteTooltipPos] = useState({ x: 0, y: 0 });

 // Optimization state
 const [suggestions, setSuggestions] = useState([]);
 const [problemLines, setProblemLines] = useState([]);
 const [optGenerating, setOptGenerating] = useState(false);
 const [highlightPair, setHighlightPair] = useState(null);

 // Zoom & Pan state
 const containerRef = useRef(null);
 const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
 const [isPanning, setIsPanning] = useState(false);
 const panStart = useRef({ x: 0, y: 0 });

 // Picker animation
 const [pickerStep, setPickerStep] = useState(0);

 useEffect(() => {
 if (view !== 'picker') return;
 const interval = setInterval(() => {
 setPickerStep(s => (s + 1) % 8);
 }, 3000);
 return () => clearInterval(interval);
 }, [view]);

 // Heatmap data fetch
 const warehouseId = warehouse === 'DE' ? 2 : 1;
 useEffect(() => {
 if (view !== 'heatmap') return;
 const params = new URLSearchParams({ period: heatPeriod });
 if (heatPickerId) params.set('picker_id', heatPickerId);
 fetch(`${API}/api/warehouse/${warehouseId}/heatmap?${params}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d) setHeatmapData(d); })
 .catch(() => {});
 }, [view, warehouseId, heatPeriod, heatPickerId]);

 // Route data fetch — available pickers for the date
 useEffect(() => {
 if (view !== 'picker') return;
 fetch(`${API}/api/warehouse/${warehouseId}/routes?date=${routeDate}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d?.pickers) setAvailablePickers(d.pickers); })
 .catch(() => {});
 }, [view, warehouseId, routeDate]);

 // Route data fetch — selected picker route
 useEffect(() => {
 if (view !== 'picker' || !routePickerId) { setRouteData(null); return; }
 setAnimStep(null); setIsPlaying(false);
 fetch(`${API}/api/warehouse/${warehouseId}/routes?date=${routeDate}&picker_id=${routePickerId}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d) setRouteData(d); })
 .catch(() => {});
 }, [view, warehouseId, routeDate, routePickerId]);

 // Compare picker route fetch
 useEffect(() => {
 if (!comparePickerId) { setCompareData(null); return; }
 fetch(`${API}/api/warehouse/${warehouseId}/routes?date=${routeDate}&picker_id=${comparePickerId}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d) setCompareData(d); })
 .catch(() => {});
 }, [warehouseId, routeDate, comparePickerId]);

 // Route animation timer
 useEffect(() => {
 if (!isPlaying || !routeData?.route) return;
 const totalPts = routeData.route.filter(p => p.x != null).length;
 if (totalPts < 2) return;
 const interval = setInterval(() => {
 setAnimStep(prev => {
 const next = (prev ?? -1) + 1;
 if (next >= totalPts) { setIsPlaying(false); return totalPts - 1; }
 return next;
 });
 }, 800 / playSpeed);
 return () => clearInterval(interval);
 }, [isPlaying, routeData, playSpeed]);

 // Optimization data fetch
 useEffect(() => {
 if (view !== 'optimize') return;
 fetch(`${API}/api/warehouse/${warehouseId}/suggestions`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => {
 if (d) {
 setSuggestions(d.suggestions || []);
 setProblemLines(d.problem_lines || []);
 }
 })
 .catch(() => {});
 }, [view, warehouseId]);

 const handleGenerateSuggestions = useCallback(async () => {
 setOptGenerating(true);
 try {
 const res = await fetch(`${API}/api/warehouse/${warehouseId}/suggestions/generate`, {
 method: 'POST', credentials: 'include',
 });
 const d = await res.json();
 if (d.suggestions) setSuggestions(d.suggestions);
 // Refresh problem lines too
 const res2 = await fetch(`${API}/api/warehouse/${warehouseId}/suggestions`, { credentials: 'include' });
 const d2 = await res2.json();
 if (d2) {
 setSuggestions(d2.suggestions || []);
 setProblemLines(d2.problem_lines || []);
 }
 } catch { /* */ }
 setOptGenerating(false);
 }, [warehouseId]);

 const handleUpdateSuggestionStatus = useCallback(async (id, status) => {
 try {
 await fetch(`${API}/api/warehouse/${warehouseId}/suggestions/${id}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ status }),
 });
 setSuggestions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
 } catch { /* */ }
 }, [warehouseId]);

 // Has real route data?
 const hasRealRouteData = routeData?.route?.length > 0;

 // Build route objects for RouteLayer
 const routeLayerData = useMemo(() => {
 const routes = [];
 if (hasRealRouteData) {
 routes.push({ route: routeData.route, color: ROUTE_COLORS[0], pickerName: routeData.picker?.name || '' });
 if (compareData?.route?.length > 0) {
 routes.push({ route: compareData.route, color: ROUTE_COLORS[1], pickerName: compareData.picker?.name || '' });
 }
 }
 return routes;
 }, [hasRealRouteData, routeData, compareData]);

 const handleRoutePointHover = useCallback((point, event) => {
 if (!point) { setRouteTooltipPoint(null); return; }
 setRouteTooltipPoint(point);
 if (event) setRouteTooltipPos({ x: event.clientX, y: event.clientY });
 }, []);

 const pickerPositions = useMemo(() => {
 return DEMO_PICKERS.map((p, i) => {
 const route = PICKER_ROUTES[i] || PICKER_ROUTES[0];
 const pos = route[pickerStep % route.length];
 const trail = [];
 for (let t = Math.max(0, pickerStep - 9); t <= pickerStep; t++) {
 trail.push(route[t % route.length]);
 }
 // Determine current zone
 let currentZone = 'warehouse.corridor';
 for (const z of ZONES) {
 if (pos.x >= z.x && pos.x <= z.x + z.w && pos.y >= z.y && pos.y <= z.y + z.h) {
 currentZone = z.nameKey;
 break;
 }
 }
 return { ...p, pos, trail, currentZone };
 });
 }, [pickerStep]);

 // Zoom with scroll
 const handleWheel = useCallback((e) => {
 e.preventDefault();
 const delta = e.deltaY > 0 ? 0.9 : 1.1;
 setTransform(t => {
 const newScale = Math.max(0.3, Math.min(3, t.scale * delta));
 // Zoom toward mouse position
 const rect = containerRef.current?.getBoundingClientRect();
 if (!rect) return { ...t, scale: newScale };
 const mx = e.clientX - rect.left;
 const my = e.clientY - rect.top;
 return {
 scale: newScale,
 x: mx - (mx - t.x) * (newScale / t.scale),
 y: my - (my - t.y) * (newScale / t.scale),
 };
 });
 }, []);

 const handleMouseDown = useCallback((e) => {
 if (e.button !== 0) return;
 setIsPanning(true);
 panStart.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
 }, [transform]);

 const handleMouseMove = useCallback((e) => {
 if (isPanning) {
 setTransform(t => ({
 ...t,
 x: e.clientX - panStart.current.x,
 y: e.clientY - panStart.current.y,
 }));
 }
 // Tooltip positioning
 setTooltipPos({ x: e.clientX, y: e.clientY });
 }, [isPanning]);

 const handleMouseUp = useCallback(() => {
 setIsPanning(false);
 }, []);

 // Reset zoom
 const resetZoom = useCallback(() => {
 setTransform({ x: 0, y: 0, scale: 1 });
 }, []);

 // Attach wheel listener
 useEffect(() => {
 const el = containerRef.current;
 if (!el) return;
 el.addEventListener('wheel', handleWheel, { passive: false });
 return () => el.removeEventListener('wheel', handleWheel);
 }, [handleWheel]);

 const foundZone = hoveredZone ? ZONES.find(z => z.id === hoveredZone) : null;

 return (
 <div className="flex flex-col h-full">
 <DemoBanner />

 {/* Header */}
 <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-5 pb-3">
 <div className="flex items-center gap-3">
 <PageHeader title="Warehouse" subtitle={t('warehouse.subtitle')} />
 <Badge variant="info">{t('warehouse.demo_mode')}</Badge>
 </div>
 <div className="flex items-center gap-3">
 {/* View toggle */}
 <div className="flex bg-surface-inset rounded-button p-0.5">
 {[
 { key: 'layout', icon: '◈', labelKey: 'warehouse.view_layout' },
 { key: 'heatmap', icon: '◉', labelKey: 'warehouse.view_heatmap' },
 { key: 'picker', Icon: User, labelKey: 'warehouse.view_picker' },
 { key: 'optimize', Icon: Lightbulb, labelKey: 'warehouse.view_optimize' },
 ].map(v => (
 <button key={v.key} onClick={() => setView(v.key)}
 className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
 view === v.key ? 'bg-surface-active text-content shadow-sm' : 'text-content-secondary hover:text-content'
 }`}
 >
 {v.Icon ? <v.Icon className="w-3.5 h-3.5" /> : <span>{v.icon}</span>}{t(v.labelKey)}
 </button>
 ))}
 </div>

 {/* Warehouse selector */}
 <select
 value={warehouse}
 onChange={e => setWarehouse(e.target.value)}
 className="bg-surface-inset border border-line-hover rounded-lg px-3 py-1.5 text-sm text-content focus:outline-none"
 >
 <option value="AT">We Ship AT</option>
 <option value="DE">We Ship DE</option>
 </select>

 {/* Config button */}
 <button onClick={() => navigate('/warehouse/config')}
 className="w-8 h-8 flex items-center justify-center rounded-lg bg-surface-inset text-content-secondary hover:text-content hover:bg-surface-hover transition-colors"
 title={t('warehouse.config.title')}>
 <Settings className="w-4 h-4" />
 </button>

 {/* Zoom controls */}
 <div className="flex items-center gap-1">
 <button onClick={() => setTransform(t => ({ ...t, scale: Math.min(3, t.scale * 1.2) }))}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset text-content-secondary hover:text-content hover:bg-surface-hover text-sm transition-colors">+</button>
 <button onClick={() => setTransform(t => ({ ...t, scale: Math.max(0.3, t.scale * 0.8) }))}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset text-content-secondary hover:text-content hover:bg-surface-hover text-sm transition-colors">−</button>
 <button onClick={resetZoom}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset text-content-secondary hover:text-content hover:bg-surface-hover text-[10px] transition-colors" title={t('warehouse.reset_zoom')}>⟲</button>
 </div>
 </div>
 </div>

 {/* Heatmap time filter */}
 {view === 'heatmap' && (
 <div className="flex items-center gap-3 px-6 pb-2">
 <div className="flex bg-surface-inset rounded-button p-0.5">
 {[
 { key: 'today', label: t('warehouse.heatmap.today') },
 { key: 'week', label: t('warehouse.heatmap.week') },
 { key: 'month', label: t('warehouse.heatmap.month') },
 ].map(f => (
 <button key={f.key} onClick={() => setHeatPeriod(f.key)}
 className={`px-3 py-1 text-xs rounded-lg transition-colors ${
 heatPeriod === f.key ? 'bg-surface-active text-content shadow-sm' : 'text-content-secondary'
 }`}
 >{f.label}</button>
 ))}
 </div>
 <HeatmapLegend t={t} />
 </div>
 )}

 {/* Main content: Floorplan + Panel */}
 <div className="flex-1 flex gap-3 px-6 pb-6 min-h-0">
 {/* Floorplan */}
 <div
 ref={containerRef}
 className="flex-1 bg-[#0f172a] rounded-card border border-line overflow-hidden relative select-none"
 style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
 onMouseDown={handleMouseDown}
 onMouseMove={handleMouseMove}
 onMouseUp={handleMouseUp}
 onMouseLeave={() => { setIsPanning(false); setHoveredZone(null); }}
 >
 <svg
 width="100%" height="100%"
 viewBox={`-20 -5 ${SVG_W + 40} ${SVG_H + 30}`}
 preserveAspectRatio="xMidYMid meet"
 style={{
 transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
 transformOrigin: '0 0',
 transition: isPanning ? 'none' : 'transform 0.15s ease-out',
 }}
 >
 {/* Grid */}
 <GridLines />

 {/* Warehouse outline */}
 <rect x={0} y={0} width={SVG_W} height={SVG_H}
 fill="none" stroke="#475569" strokeWidth={2} rx={4} />

 {/* Main horizontal corridor at y:48m */}
 <rect x={0} y={46 * SCALE} width={SVG_W} height={3 * SCALE}
 fill="#1e293b" opacity={0.7} />

 {/* Vertical corridors */}
 <rect x={18 * SCALE} y={0} width={2 * SCALE} height={48 * SCALE}
 fill="#1e293b" opacity={0.5} />
 <rect x={40 * SCALE} y={0} width={2 * SCALE} height={50 * SCALE}
 fill="#1e293b" opacity={0.5} />

 {/* Zones */}
 {ZONES.map(z => (
 <g key={z.id}
 onMouseEnter={() => setHoveredZone(z.id)}
 onMouseLeave={() => setHoveredZone(null)}
 className="cursor-pointer"
 >
 <rect
 x={z.x * SCALE} y={z.y * SCALE} width={z.w * SCALE} height={z.h * SCALE}
 fill={z.color} fillOpacity={hoveredZone === z.id ? 0.2 : 0.1}
 stroke={z.color} strokeWidth={hoveredZone === z.id ? 2 : 1.2}
 strokeOpacity={hoveredZone === z.id ? 1 : 0.6}
 rx={4}
 style={{
 filter: `drop-shadow(0 0 ${hoveredZone === z.id ? 12 : 6}px ${z.color}33)`,
 transition: 'all 0.3s ease',
 }}
 />
 {/* Internal detail */}
 <RackDetail zone={z} />
 <BlockDetail zone={z} />
 <PackDetail zone={z} />
 {/* Label */}
 <text x={(z.x + z.w / 2) * SCALE} y={(z.y + z.h / 2 - 0.5) * SCALE}
 fill={z.color} fontSize={12} fontWeight={700} textAnchor="middle"
 fontFamily="inherit" opacity={0.9}>
 {t(z.nameKey)}
 </text>
 {z.subKey && (
 <text x={(z.x + z.w / 2) * SCALE} y={(z.y + z.h / 2 + 1.2) * SCALE}
 fill={z.color} fontSize={9} textAnchor="middle"
 fontFamily="inherit" opacity={0.6}>
 {t(z.subKey)}
 </text>
 )}
 </g>
 ))}

 {/* Gates */}
 {GATES.map(g => (
 <g key={g.id}>
 <rect x={g.x * SCALE} y={g.y * SCALE} width={g.w * SCALE} height={g.h * SCALE}
 fill={g.color} fillOpacity={0.15} stroke={g.color} strokeWidth={1} strokeOpacity={0.5} rx={3} />
 <text x={(g.x + g.w / 2) * SCALE} y={(g.y + g.h / 2 + 0.5) * SCALE}
 fill={g.color} fontSize={9} fontWeight={600} textAnchor="middle"
 fontFamily="inherit" opacity={0.7}>
 {t(g.nameKey, { n: g.gateNum })}
 </text>
 </g>
 ))}

 {/* Scale markers */}
 <ScaleMarkers />

 {/* North arrow */}
 <g transform="translate(14, 14)">
 <circle r={12} fill="#1e293b" stroke="#475569" strokeWidth={1} />
 <text x={0} y={4} fill="#94a3b8" fontSize={12} fontWeight={700} textAnchor="middle" fontFamily="inherit">N</text>
 </g>

 {/* Heatmap Canvas overlay */}
 {view === 'heatmap' && (
 <HeatmapCanvas
 points={heatmapData?.points}
 whW={WH_W} whH={WH_H}
 scale={SCALE}
 opacity={heatOpacity}
 />
 )}

 {/* Route / Picker overlay */}
 {view === 'picker' && (
 hasRealRouteData ? (
 <RouteLayer routes={routeLayerData} animStep={animStep} onHoverPoint={handleRoutePointHover} scale={SCALE} />
 ) : (
 <DemoPickerLayer pickers={pickerPositions} selected={selectedPicker} onSelect={setSelectedPicker} scale={SCALE} />
 )
 )}

 {/* Optimization problem lines overlay */}
 {view === 'optimize' && (
 <OptimizationLayer problemLines={problemLines} highlightPair={highlightPair} scale={SCALE} />
 )}
 </svg>

 {/* Zoom level indicator */}
 <div className="absolute bottom-3 left-3 text-[10px] font-mono text-content-tertiary bg-[#0f172a]/80 px-2 py-1 rounded-lg">
 {Math.round(transform.scale * 100)}%
 </div>
 </div>

 {/* Right panel */}
 {view === 'optimize' ? (
 <OptimizationPanel
 suggestions={suggestions} problemLines={problemLines}
 generating={optGenerating} onGenerate={handleGenerateSuggestions}
 onUpdateStatus={handleUpdateSuggestionStatus}
 highlightPair={highlightPair} setHighlightPair={setHighlightPair}
 t={t} locale={locale}
 />
 ) : view === 'picker' ? (
 <RoutePanel
 routeData={routeData} compareData={compareData}
 availablePickers={availablePickers}
 pickerId={routePickerId} setPickerId={setRoutePickerId}
 comparePickerId={comparePickerId} setComparePickerId={setComparePickerId}
 routeDate={routeDate} setRouteDate={setRouteDate}
 isPlaying={isPlaying} setIsPlaying={setIsPlaying}
 playSpeed={playSpeed} setPlaySpeed={setPlaySpeed}
 animStep={animStep} setAnimStep={setAnimStep}
 t={t} locale={locale}
 isDemo={!hasRealRouteData}
 />
 ) : view === 'heatmap' ? (
 <HeatmapPanel
 data={heatmapData}
 period={heatPeriod} setPeriod={setHeatPeriod}
 pickerId={heatPickerId} setPickerId={setHeatPickerId}
 heatOpacity={heatOpacity} setHeatOpacity={setHeatOpacity}
 t={t} locale={locale}
 />
 ) : (
 <InfoPanel zones={ZONES} />
 )}
 </div>

 {/* Bottom legend (layout view) */}
 {view === 'layout' && (
 <div className="px-6 pb-4 flex flex-wrap items-center gap-3">
 {ZONES.map(z => (
 <div key={z.id} className="flex items-center gap-1.5">
 <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: z.color, opacity: 0.7 }} />
 <span className="text-[11px] text-content-secondary">{t(z.nameKey)}</span>
 </div>
 ))}
 <div className="flex items-center gap-1.5">
 <div className="w-3 h-3 rounded-sm bg-slate-500/50" />
 <span className="text-[11px] text-content-secondary">{t('warehouse.loading_gates')}</span>
 </div>
 </div>
 )}

 {/* Tooltips */}
 <ZoneTooltip zone={foundZone} pos={tooltipPos} />
 <RoutePointTooltip point={routeTooltipPoint} pos={routeTooltipPos} />
 </div>
 );
}
